type FieldOptions = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	value !== null && typeof value === "object" && (Object.getPrototypeOf(value) === null || Object.getPrototypeOf(value)?.constructor?.name === "Object");

// Metadata may contain callbacks; JSON serialization and structuredClone lose them.
const clone = (value: unknown): unknown => {
	if (Array.isArray(value)) return value.map(clone);
	if (isRecord(value)) return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
	return value;
};

const fieldOptions = (record: Record<string, unknown>, name: string): FieldOptions => {
	const prefix = `$__${name}_`;
	const flat = Object.fromEntries(Object.entries(record).filter(([key]) => key.startsWith(prefix)).map(([key, value]) => [key.slice(prefix.length), value]));
	const options = record[`$__${name}`];
	return { ...flat, ...(isRecord(options) ? options : {}) };
};

/** Create a fresh editable row without carrying over the preceding row's values. */
export const createEmptyFormItem = (value: unknown, options: FieldOptions = {}): unknown => {
	if (Object.prototype.hasOwnProperty.call(options, "default")) return clone(options.default);
	// Display content and locked configuration are not user input.
	if (options.disabled || ["plain", "button", "href", "link", "spacer", "delete_button"].includes(String(options.type))) return clone(value);
	if (Array.isArray(value)) {
		if (!value.length || Array.isArray(options.possible_values)) return [];
		const declaredMin = Number(options.min ?? (options.optional ? 0 : 1));
		// Keep a schema row unless metadata can recreate an empty list.
		const min = Math.max(options.item_template !== undefined ? 0 : 1, Number.isSafeInteger(declaredMin) && declaredMin >= 0 ? declaredMin : 1);
		return Array.from({ length: min }, () => createEmptyFormItem(value[0], { type: options.type }));
	}
	if (isRecord(value)) return Object.fromEntries(Object.entries(value).map(([key, item]) => [
		key,
		key.startsWith("$__") || key.startsWith("$link_") ? clone(item) : createEmptyFormItem(item, fieldOptions(value, key)),
	]));
	if (typeof value === "boolean" || options.type === "boolean" || options.type === "switchbox") return false;
	if (["select", "radio", "country", "date", "file", "password", "sha256"].includes(String(options.type))) return "";
	// Preserve numeric storage types; decimal strings remain strings to avoid precision loss.
	if (typeof value === "number") return 0;
	if (typeof value === "string") return "";
	return value; // null/undefined and opaque values retain their representation.
};
